import type { ReportPeriod, SentimentReportQuery } from '@discovr/types';

/**
 * Canonical React Query keys for the campaign-detail surface.
 * All fetch queries and all SSE invalidations use these — single source of truth.
 * Keep tuples `as const` so TS infers literal element types — required
 * for QueryKey type narrowing in React Query v5.
 */
import type { OutreachDraftsParams, OutreachRatesParams, OutreachRowsParams } from './api/outreachApi';

/**
 * Shared tab IDs for the campaign-detail page. Both admin and brand use
 * these core tabs; each app extends with role-specific extras:
 *   admin: 'brief'
 *   brand: 'overview' | 'live'
 */
export type CampaignTabId =
  | 'details'
  | 'reporting'
  | 'creators'
  | 'confirmed'
  | 'scripts'
  | 'content'
  | 'live-content';

export type RosterTab =
  | 'creators'
  | 'rejected'
  | 'scripts'
  | 'content'
  | 'finalized'
  | 'live-content';

export type ThreadType = 'selection' | 'script' | 'content';

/** The one content-thread discriminator — shared by the `conversation` key
 *  and by the REST paths in api/campaignQueries.ts, so a
 *  key and the endpoint it caches can never name the thread differently.
 *  `as const satisfies` rather than a `: ThreadType` annotation: the keys below
 *  stay literal tuples (see this file's header) while still being checked
 *  against the union. */
export const CONTENT_THREAD_TYPE = 'content' as const satisfies ThreadType;

/** Server-side sort params, shared by the roster query key (RosterFilters) and
 *  the list-endpoint params (UnifiedListParams) so the two can't drift apart. */
export interface SortParams {
  /** Active column sort field (a roster column id, e.g. followers / est_cpv).
   *  Part of the cache key so each sort is a distinct query (re-fetched from page
   *  1), never a client re-slice of a differently-sorted page. */
  sort_by?: string;
  /** Sort direction. Absent lets the server pick its per-field default. */
  sort_dir?: 'asc' | 'desc';
}

export interface RosterFilters extends SortParams {
  status?: string;
  page?: number;
  limit?: number;
  /** Client-side-only bucket discriminator for query keys whose BE endpoint
   *  has no status filter (e.g. roster/finalized) — gives each pill its own
   *  cache slot despite every pill hitting the identical underlying request.
   *  Never sent to the BE (unifiedRosterQs doesn't read it). */
  phase?: string;
  /** Search term — part of the cache key so each query is cached per search. */
  search?: string;
}

/** The page, size and sort a Creator Content request carries; each combination is its own cache entry. */
export interface CreatorContentParams {
  page: number;
  limit: number;
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
  /** Trimmed, non-empty creator search; absent when not searching. */
  search?: string;
}

const reportsPrefix = (id: string) => ['campaign-report', id] as const;
const DEFAULT_PERIOD_KEY = 'default';
export const SENTIMENT_CREATOR_PAGE_SIZE = 50;
/** Largest `creator_limit` the sentiment report endpoint accepts. */
export const SENTIMENT_CREATOR_MAX_PAGE_SIZE = 200;

export const campaignKeys = {
  all: ['campaign'] as const,
  detail: (id: string) => ['campaign', 'detail', id] as const,
  editRequestCurrent: (id: string) => ['campaign', 'edit-request', 'current', id] as const,
  editRequestHistory: (id: string) => ['campaign', 'edit-request', 'history', id] as const,

  rosterAll: (id: string) => ['roster', id] as const,
  rosterTab: (id: string, tab: RosterTab, filters?: RosterFilters) =>
    ['roster', id, tab, filters ?? {}] as const,

  /** Tab-aggregated KPI keys served by /roster-kpis/{tab}. Separate from
   *  the tab list query so a list page-change doesn't refetch the KPI
   *  and an SSE-driven KPI refresh doesn't refetch the list. */
  rosterKpisCreators: (id: string) => ['roster-kpis', id, 'creators'] as const,
  rosterKpisFinalized: (id: string) => ['roster-kpis', id, 'finalized'] as const,
  /** Prefix of every roster-KPI key above, for invalidating all tabs at once. */
  rosterKpisAll: (id: string) => ['roster-kpis', id] as const,

  /** ['roster', id, 'counts'] — consolidated per-status + per-phase counts.
   *  Sits under the ['roster', id] prefix so the existing SSE rosterPredicate
   *  auto-invalidates it alongside the tab lists. */
  rosterCounts: (id: string) => ['roster', id, 'counts'] as const,

  conversation: (entryId: string, type: ThreadType) =>
    ['conversation', entryId, type] as const,

  /** The full, unpaginated anchor set for one script version — see
   *  useAnchoredMessages. Keyed by versionId (not just entryId/type) so a
   *  version switch is a fresh query, not a stale cache hit. */
  anchoredMessages: (entryId: string, type: ThreadType, versionId: string) =>
    ['conversation', entryId, type, 'anchors', versionId] as const,

  /** One attachment's bytes, keyed by file id — see useThreadFile. `fileId`
   *  is normalised to `''` when absent (mirrors linkFunnelSummary's
   *  optional-param convention below) so the key stays stable while the
   *  query itself is disabled without one. */
  threadFile: (entryId: string, fileId: string | undefined) =>
    ['roster', entryId, 'thread-file', fileId ?? ''] as const,

  /** The server's upload rules for one entry's content thread — see useThreadFilePolicy. */
  threadFilePolicy: (entryId: string) => ['roster', entryId, 'thread-file-policy'] as const,

  scriptDetail: (entryId: string) => ['roster', entryId, 'script-detail'] as const,
  contentDetail: (entryId: string) => ['roster', entryId, 'content-detail'] as const,
  contentVideoUrl: (entryId: string) => ['roster', entryId, 'content-video-url'] as const,
  creatorAnalytics: (entryId: string) =>
    ['roster', entryId, 'analytics-metrics'] as const,

  /** ['campaign-last-import', id] — used by useLastImportResult + SSE setQueryData. */
  lastImport: (campaignId: string) => ['campaign-last-import', campaignId] as const,

  /** ['campaigns-list', page, limit] — specific page; prefix ['campaigns-list'] invalidates all. */
  campaignList: (page: number, limit: number) => ['campaigns-list', page, limit] as const,
  /** Prefix key — invalidates every page of the campaign list. */
  campaignListAll: () => ['campaigns-list'] as const,

  /** ['campaigns-list','all'] — the fetch-all (whole-dataset) query for
   *  client-side search/pagination. Sits UNDER the ['campaigns-list'] prefix so
   *  campaignListAll() invalidation already covers it (no extra effect needed). */
  campaignListFull: () => ['campaigns-list', 'all'] as const,

  /** ['roster-resolve-creator', campaignId, username] — resolve which tab/pill/page
   *  a creator lands on in a given campaign. Used by the shareable-link relanding hook. */
  resolveCreator: (campaignId: string, username: string) =>
    ['roster-resolve-creator', campaignId, username] as const,

  /** ['campaign', 'share-grants', campaignId] — authed share-grant list for one
   *  campaign (mint/list/revoke). Invalidated by useMintShareGrant/useRevokeShareGrant. */
  shareGrants: (id: string) => ['campaign', 'share-grants', id] as const,

  /** ['campaign', 'link-funnel-summary', campaignId, window] — campaign
   *  share-link funnel summary (device split, public/private split,
   *  drop-off, conversions). */
  linkFunnelSummary: (id: string, window?: string) => ['campaign', 'link-funnel-summary', id, window ?? ''] as const,

  /** Reporting tabs' finished data, all under ['campaign-report', id] — a prefix of its own,
   *  so a roster/KPI invalidation never refetches a report it does not name. */
  reports: (id: string) => reportsPrefix(id),
  generalReports: (id: string) => [...reportsPrefix(id), 'general'] as const,
  generalReport: (id: string, period?: ReportPeriod) =>
    [...reportsPrefix(id), 'general', period ?? DEFAULT_PERIOD_KEY] as const,
  sentimentReports: (id: string) => [...reportsPrefix(id), 'sentiment'] as const,
  sentimentReport: (id: string, q: SentimentReportQuery) =>
    [
      ...reportsPrefix(id),
      'sentiment',
      q.from ?? 'start',
      q.to ?? 'today',
      q.creatorLimit ?? SENTIMENT_CREATOR_PAGE_SIZE,
      q.creatorOffset ?? 0,
    ] as const,
  audienceReport: (id: string) => [...reportsPrefix(id), 'audience'] as const,
  creatorContentReports: (id: string) => [...reportsPrefix(id), 'creator-content'] as const,
  creatorContentReport: (id: string, params: CreatorContentParams) =>
    [...reportsPrefix(id), 'creator-content', params] as const,

  /** ['campaign-report', id, 'exports'] — the export history list; under the report prefix so a reconnect refresh covers it. */
  reportExports: (id: string) => [...reportsPrefix(id), 'exports'] as const,

  /** ['campaign', 'report-export', id] — MUTATION key of the Excel export. Lets any mounted
   *  button (including one remounted mid-flight) see that an export is already running. */
  reportExport: (id: string) => ['campaign', 'report-export', id] as const,

  /** ['campaign', id, 'outreach', 'counts'] — Outreach tab-pill counts (v2 sourcing). */
  outreachCounts: (id: string) => ['campaign', id, 'outreach', 'counts'] as const,
  /** ['campaign', id, 'outreach', 'rows', params] — one Outreach page's rows, keyed
   *  by every param so each tab/search/sort/page is its own cache slot. */
  outreachRows: (id: string, params: OutreachRowsParams) => ['campaign', id, 'outreach', 'rows', params] as const,
  /** Prefix key — invalidates every Outreach rows page for this campaign. */
  outreachRowsAll: (id: string) => ['campaign', id, 'outreach', 'rows'] as const,
  outreachImport: (id: string, importId: number) => ['campaign', id, 'outreach', 'import', importId] as const,
  outreachOperation: (id: string, operationId: string) =>
    ['campaign', id, 'outreach', 'operation', operationId] as const,
  /** ['campaign', id, 'outreach', 'operations', 'active'] — the running operations list. */
  outreachActiveOperations: (id: string) => ['campaign', id, 'outreach', 'operations', 'active'] as const,
  /** ['campaign', id, 'outreach', 'bulk-sends'] — the Bulk sends not yet sent (the header list). */
  firstTouchDrafts: (entryId: string) => ['campaign', 'outreach', 'first-touch-drafts', entryId] as const,
  outreachBulkSends: (id: string) => ['campaign', id, 'outreach', 'bulk-sends'] as const,

  /** ['campaign', id, 'outreach', 'templates'] — the grouped global/brand/campaign library. */
  outreachTemplates: (id: string) => ['campaign', id, 'outreach', 'templates'] as const,
  /** ['campaign', id, 'outreach', 'settings'] — time zone, pause state. */
  outreachSettings: (id: string) => ['campaign', id, 'outreach', 'settings'] as const,
  /** ['campaign', id, 'outreach', 'pool'] — the send dialog's mailbox picker. */
  outreachPool: (id: string) => ['campaign', id, 'outreach', 'pool'] as const,
  /** Entry-scoped, not campaign-scoped: a thread is read from the roster entry route. */
  outreachThread: (entryId: string) => ['outreach', 'thread', entryId] as const,
  /** Under the thread key, so every action and SSE event that refreshes the thread refreshes its timeline. */
  outreachEvents: (entryId: string) => ['outreach', 'thread', entryId, 'events'] as const,
  outreachMessage: (entryId: string, messageId: number) => ['outreach', 'thread', entryId, 'message', messageId] as const,
  /** The thread composer's saved, unsent reply. */
  outreachComposerDraft: (entryId: string) => ['outreach', 'composer-draft', entryId] as const,
  /** Deployment-wide send-hold + pool counts; no campaign in the key. */
  outreachStatus: () => ['outreach', 'status'] as const,

  /** ['campaign', id, 'outreach', 'rows', 'row', entryId] — nested under
   *  outreachRowsAll on purpose: every invalidation that refreshes the table
   *  refreshes the single row too (ruling 60). */
  outreachRow: (campaignId: string, entryId: string) => [...campaignKeys.outreachRowsAll(campaignId), 'row', entryId] as const,
  /** Prefix key — invalidates every drafts page for this campaign. */
  outreachDraftsAll: (campaignId: string) => [...campaignKeys.detail(campaignId), 'outreach', 'drafts'] as const,
  /** `scope` (the wizard batch id) goes last so the outreachDraftsAll prefix still matches every scoped page. */
  outreachDrafts: (campaignId: string, params: OutreachDraftsParams, scope?: string) =>
    [...campaignKeys.outreachDraftsAll(campaignId), params.kind ?? 'all', params.page ?? 1, params.limit ?? 25, ...(scope ? [scope] : [])] as const,
  outreachKpis: (campaignId: string) => [...campaignKeys.detail(campaignId), 'outreach', 'kpis'] as const,
  /** ['campaign', id, 'outreach', 'mailbox-health'] — the CRM's mailbox-health card (CRM v2 contract §2). Under the
   *  ['campaign', id, 'outreach'] prefix so useOutreachSSE's reconnect refresh covers it. */
  outreachMailboxHealth: (campaignId: string) => ['campaign', campaignId, 'outreach', 'mailbox-health'] as const,
  /** Prefix key — invalidates every Rates page for this campaign; same reconnect-covered prefix. */
  outreachRatesAll: (campaignId: string) => ['campaign', campaignId, 'outreach', 'rates'] as const,
  outreachRates: (campaignId: string, params: OutreachRatesParams) => [...campaignKeys.outreachRatesAll(campaignId), params] as const,
} as const;
